import React, { Component } from "react";
import Helmet from "react-helmet";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import LazyImage from "../LazyImage/LazyImage";
import {
  FormControl,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  ListItemText,
  Chip,
  FormControlLabel,
  Switch
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import CropPortraitIcon from "@material-ui/icons/CropPortrait";
import FlareIcon from "@material-ui/icons/Flare";
import DeleteIcon from "@material-ui/icons/Delete";
import LockIcon from "@material-ui/icons/Lock";
import PublicIcon from "@material-ui/icons/Public";
import WarningIcon from "@material-ui/icons/Warning";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

import constants from "../Constants/constants";
import { fetchCardNames } from "../Search/cardNameCache";
import {
  performCardSearch,
  ELEMENT_NAMES, RARITY_OPTIONS, CARD_TYPES, TURN_COUNTS,
  ABILITY_COSTS, COMBAT_ATTRIBUTES, CARD_SETS
} from "../Search/searchUtils";
import { auth } from "../AuthService";
import LoginModal from "../LoginModal/LoginModal";
import DeckPreviewModal from "./DeckPreviewModal";
import PlaymatView from "./PlaymatView";
import ConfirmModal from "./ConfirmModal";
import CardDetailModal from "./CardDetailModal";
import {
  RARITY_ORDER,
  getUniqueCards,
  getDeckStats,
  getCardCount,
  getExportText,
  sortResults,
  shuffleArray,
  getAbilityCostCount,
  getAbilityCostElements,
  isUnlimitedCard,
  getDualSideName,
} from "./deckUtils";

import "./DeckBuilder.css";

export default class DeckBuilder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Search state
      search: "",
      characteristic: "",
      cardEffect: "",
      rarityQuery: [],
      cardSetQuery: [],
      elementsQuery: [],
      typeQuery: [],
      turnCountQuery: [],
      combatQuery: [],
      abilityCostQuery: [],
      abilityCostElementQuery: [],
      dualElementsOnly: false,
      hideKickstarter: true,
      resultsLoading: false,
      results: [],
      hasSearched: false,
      suggestions: [],
      showSuggestions: false,

      // Deck state
      deckName: "My Deck",
      deckCards: [],        // array of card objects (duplicates = copies)
      deckImage: "",
      showDeckTray: false,  // mobile tray visibility
      apiDeckId: null,      // MongoDB _id of currently loaded deck
      deckIsPublic: true,   // visibility toggle for save
      savedDecks: [],       // list from API
      deckSortNewest: true, // true = newest first, false = oldest first

      // Auth state
      isLoggedIn: false,
      currentUser: null,
      showLoginModal: false,
      savingDeck: false,
      loadingDecks: false,
      deckDirty: false,       // true when deck has unsaved changes
      justSaved: false,       // true briefly after a successful save

      // UI state
      darkMode: false,
      toast: null,
      selectedCard: null,
      touchStartX: null,
      deckTrayView: "current", // "current" or "collection"
      previewDeck: null,       // deck object for preview modal
      modalFromPreview: false,  // true when card modal opened from preview
      showPrivateSwapModal: false,  // confirmation modal for swapping private deck
      privateSwapDeck: null,        // the existing private deck to swap
      showDeleteModal: false,       // confirmation modal for deleting a deck
      deleteDeckTarget: null,       // deck to delete
      showClearModal: false,        // confirmation modal for clearing deck
      previewGroupByTurn: false, // toggle turn sub-grouping in preview
      trayGroupByTurn: false,    // toggle turn sub-grouping in deck tray

      // Side deck state
      sideDeckCards: [],
      deckSubView: "main", // "main" or "side"

      // Test hand state
      testHandCards: [],
      testHandRemaining: [],
      testHandActive: false,
      testHandSelected: new Set(),

      // Playmat state
      showPlaymat: false,
      playmatDeck: null,

      // Export state
      showExport: false,
      exportCopied: false,

      // Results sort state
      sortBy: "name",
      sortDirection: "asc",

      // Rarity effects
      showRarityEffects: true,

      // Card usage stats
      cardUsageStats: null,

      // Back to top
      showBackToTop: false,
    };
    this.searchTimeout = null;
    this.toastTimeout = null;
    this.exportCopiedTimeout = null;
    this.savedTimeout = null;
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
    document.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("scroll", this.handleScroll);
    const savedTheme = localStorage.getItem("searchPageTheme");
    const isDark = savedTheme === null ? false : savedTheme === "dark";
    this.setBodyBackground(isDark);
    if (savedTheme !== null) {
      this.setState({ darkMode: isDark });
    }
    const savedRarityEffects = localStorage.getItem("showRarityEffects");
    if (savedRarityEffects !== null) {
      this.setState({ showRarityEffects: savedRarityEffects === "true" });
    }
    fetchCardNames();

    // Fetch card usage stats (non-blocking)
    fetch(`${constants.DOMAIN}api/v1/guest/deck/stats`)
      .then(res => res.json())
      .then(data => { if (this._mounted) this.setState({ cardUsageStats: data }); })
      .catch(() => {});

    // Check auth state
    if (auth.loggedIn()) {
      const user = auth.getUser();
      this.setState({ isLoggedIn: true, currentUser: user });
      this.fetchMyDecks();
    }

    // Listen for login/logout from Navbar
    window.addEventListener("authChange", this.handleAuthChange);

    // Refresh decks when tab becomes visible (sync across tabs)
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === "visible" && auth.loggedIn()) {
      this.fetchMyDecks();
    }
  }

  handleAuthChange = () => {
    if (auth.loggedIn()) {
      const user = auth.getUser();
      this.setState({ isLoggedIn: true, currentUser: user });
      this.fetchMyDecks();
    } else {
      this.setState({ isLoggedIn: false, currentUser: null, savedDecks: [], apiDeckId: null, deckCards: [], sideDeckCards: [], deckName: "My Deck", deckImage: "", deckIsPublic: true, showExport: false, deckSubView: "main" });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.darkMode !== this.state.darkMode) {
      this.setBodyBackground(this.state.darkMode);
    }

    const filterKeys = [
      "search", "characteristic", "cardEffect", "rarityQuery",
      "cardSetQuery", "elementsQuery", "typeQuery", "turnCountQuery",
      "combatQuery", "dualElementsOnly", "hideKickstarter",
      "abilityCostQuery", "abilityCostElementQuery"
    ];

    const filtersChanged = filterKeys.some(key => {
      if (Array.isArray(this.state[key])) {
        return JSON.stringify(prevState[key]) !== JSON.stringify(this.state[key]);
      }
      return prevState[key] !== this.state[key];
    });

    if (filtersChanged) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.doSearch();
      }, 400);
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    if (this.exportCopiedTimeout) clearTimeout(this.exportCopiedTimeout);
    if (this.savedTimeout) clearTimeout(this.savedTimeout);
    document.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("authChange", this.handleAuthChange);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    document.documentElement.style.backgroundColor = "";
    document.documentElement.style.overflow = "";
    document.body.style.backgroundColor = "";
    document.body.style.overflow = "";
  }

  setBodyBackground(isDark) {
    const bgColor = isDark ? "#0d0d1a" : "#9D0208";
    document.documentElement.style.backgroundColor = bgColor;
    document.body.style.backgroundColor = bgColor;
  }

  hasFilters() {
    const { search, characteristic, cardEffect, rarityQuery, cardSetQuery,
      elementsQuery, typeQuery, turnCountQuery, combatQuery, abilityCostQuery,
      abilityCostElementQuery, dualElementsOnly, hideKickstarter } = this.state;
    return search.trim() || characteristic.trim() || cardEffect.trim() ||
      rarityQuery.length > 0 || cardSetQuery.length > 0 ||
      elementsQuery.length > 0 || typeQuery.length > 0 ||
      turnCountQuery.length > 0 || combatQuery.length > 0 ||
      abilityCostQuery.length > 0 || abilityCostElementQuery.length > 0 ||
      dualElementsOnly || !hideKickstarter;
  }

  async doSearch() {
    if (!this.hasFilters()) {
      this.setState({ results: [], hasSearched: false });
      return;
    }

    this.setState({ resultsLoading: true, hasSearched: true });

    try {
      const result = await performCardSearch(this.state, {
        getAbilityCostCount: getAbilityCostCount,
        getAbilityCostElements: getAbilityCostElements,
      });

      if (result.suggestions.length > 0) {
        this.setState({ results: [], resultsLoading: false, suggestions: result.suggestions, showSuggestions: true });
      } else {
        this.setState({ results: result.posts, resultsLoading: false, suggestions: [], showSuggestions: false });
      }
    } catch (err) {
      console.error(err);
      this.setState({ resultsLoading: false });
    }
  }

  resetFilters = () => {
    this.setState({
      search: "", characteristic: "", cardEffect: "",
      rarityQuery: [], cardSetQuery: [], elementsQuery: [],
      typeQuery: [], turnCountQuery: [], combatQuery: [],
      abilityCostQuery: [], abilityCostElementQuery: [],
      results: [], hasSearched: false,
      dualElementsOnly: false, hideKickstarter: true,
      suggestions: [], showSuggestions: false
    });
  }

  handleSuggestionClick = (cardName) => {
    this.setState({ search: cardName, suggestions: [], showSuggestions: false });
  }

  handleMultiSelectChange = (stateKey) => (event) => {
    const newValue = event.target.value;
    if (stateKey === "typeQuery") {
      const updates = { [stateKey]: newValue };
      const hasAbility = newValue.includes("Ability");
      const hasTurnCountTypes = newValue.includes("Character") || newValue.includes("Strategy");
      if (!hasAbility) {
        if (this.state.abilityCostQuery.length > 0) updates.abilityCostQuery = [];
        if (this.state.abilityCostElementQuery.length > 0) updates.abilityCostElementQuery = [];
      }
      if (!hasTurnCountTypes && newValue.length > 0 && this.state.turnCountQuery.length > 0) {
        updates.turnCountQuery = [];
      }
      this.setState(updates);
    } else {
      this.setState({ [stateKey]: newValue });
    }
  }

  // ========================
  // MODAL / CARD DETAIL
  // ========================

  handleScroll = () => {
    this.setState({ showBackToTop: window.scrollY > 400 });
  }

  scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  handleKeyDown = (e) => {
    if (!this.state.selectedCard) return;
    if (e.key === "Escape") this.closeModal();
    else if (e.key === "ArrowLeft") { e.preventDefault(); this.goToPrevCard(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); this.goToNextCard(); }
  }

  openModal = (card, fromPreview = false) => {
    this.setState({ selectedCard: card, modalFromPreview: fromPreview });
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  }

  closeModal = () => {
    const wasFromPreview = this.state.modalFromPreview;
    this.setState({ selectedCard: null, modalFromPreview: false });
    if (!wasFromPreview) {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    }
  }

  getSortedResults = () => {
    return sortResults(this.state.results, this.state.sortBy, this.state.sortDirection, RARITY_ORDER);
  }

  getCurrentCardIndex = () => {
    const { selectedCard, results } = this.state;
    if (!selectedCard || results.length === 0) return -1;
    const sorted = this.getSortedResults();
    return sorted.findIndex(card => card._id === selectedCard._id);
  }

  goToPrevCard = () => {
    const sorted = this.getSortedResults();
    const idx = this.getCurrentCardIndex();
    if (idx > 0) this.setState({ selectedCard: sorted[idx - 1] });
  }

  goToNextCard = () => {
    const sorted = this.getSortedResults();
    const idx = this.getCurrentCardIndex();
    if (idx < sorted.length - 1) this.setState({ selectedCard: sorted[idx + 1] });
  }

  handleTouchStart = (e) => {
    if (e.touches.length > 1) { this.setState({ touchStartX: null }); return; }
    this.setState({ touchStartX: e.touches[0].clientX });
  }

  handleTouchEnd = (e) => {
    const { touchStartX } = this.state;
    if (touchStartX === null) return;
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) this.goToNextCard();
      else this.goToPrevCard();
    }
    this.setState({ touchStartX: null });
  }

  handleFlipCard = async (otherName) => {
    // Search current results for the other side
    const found = this.state.results.find(c => (c.cardName || c.name) === otherName);
    if (found) {
      this.setState({ selectedCard: found });
      return;
    }
    // Fetch from API
    try {
      const res = await fetch(`${constants.DOMAIN}api/v1/guest/post?search=${encodeURIComponent(otherName)}`);
      const data = await res.json();
      const cards = data.posts || [];
      const exact = cards.find(c => (c.cardName || c.name) === otherName);
      if (exact) {
        this.setState({ selectedCard: exact });
      } else if (cards.length > 0) {
        this.setState({ selectedCard: cards[0] });
      }
    } catch (err) {
      console.error("Failed to fetch dual-sided card:", err);
    }
  }

  showToast = (message) => {
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.setState({ toast: message });
    this.toastTimeout = setTimeout(() => {
      this.setState({ toast: null });
    }, 2000);
  }

  fetchMyDecks = async () => {
    this.setState({ loadingDecks: true });
    try {
      const res = await auth.authFetch(`${constants.DOMAIN}api/v1/decks/`);
      const decks = await res.json();
      const savedDecks = decks.map(d => ({
        id: d._id,
        name: d.name,
        cards: (d.deck || []).filter(Boolean), // full card objects from populate
        sideDeck: (d.sideDeck || []).filter(Boolean),
        image: d.image,
        isPublic: d.isPublic,
        updatedAt: d.updatedAt,
      })).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      this.setState({ savedDecks, loadingDecks: false });
      return savedDecks;
    } catch {
      this.setState({ loadingDecks: false });
      return [];
    }
  }

  // ========================
  // DECK MANAGEMENT
  // ========================

  addCardToDeck = async (card) => {
    const { deckCards } = this.state;
    const isField = card.cardType === "Field";
    const dualSide = getDualSideName(card);

    if (isField) {
      const fieldCards = deckCards.filter(c => c.cardType === "Field");
      if (fieldCards.length >= 1) {
        // Allow if the only existing field cards are the paired side of this card
        const allArePaired = dualSide && fieldCards.every(c => (c.cardName || c.name) === dualSide);
        if (!allArePaired) {
          this.showToast("Max 1 field card per deck");
          return;
        }
      }
    } else if (!isUnlimitedCard(card)) {
      const copyCount = deckCards.filter(c => c._id === card._id).length;
      if (copyCount >= 3) {
        this.showToast("Max 3 copies of any card");
        return;
      }
    }

    let newDeckCards = [...deckCards, card];
    const deckImage = this.state.deckImage || card.image;

    // Auto-add the dual-sided partner if it's not already in the deck
    if (isField && dualSide) {
      const partnerAlready = deckCards.some(c => (c.cardName || c.name) === dualSide);
      if (!partnerAlready) {
        // Check current search results first
        let partner = this.state.results.find(c => (c.cardName || c.name) === dualSide);
        if (!partner) {
          // Fetch from API
          try {
            const res = await fetch(`${constants.DOMAIN}api/v1/guest/post?search=${encodeURIComponent(dualSide)}`);
            const data = await res.json();
            const cards = data.posts || [];
            partner = cards.find(c => (c.cardName || c.name) === dualSide);
          } catch (err) {
            console.error("Failed to fetch dual-sided partner:", err);
          }
        }
        if (partner) {
          newDeckCards = [...newDeckCards, partner];
        }
      }
    }

    this.setState({ deckCards: newDeckCards, deckImage, deckDirty: true, justSaved: false });
  }

  removeCardFromDeck = (cardId) => {
    const { deckCards } = this.state;
    const idx = deckCards.findIndex(c => c._id === cardId);
    if (idx === -1) return;

    const removedCard = deckCards[idx];
    let newDeckCards = [...deckCards];
    newDeckCards.splice(idx, 1);

    // If removing a dual-sided field card, also remove its partner
    if (removedCard.cardType === "Field") {
      const dualSide = getDualSideName(removedCard);
      if (dualSide) {
        newDeckCards = newDeckCards.filter(c => (c.cardName || c.name) !== dualSide);
      }
    }

    this.setState({ deckCards: newDeckCards, deckDirty: true, justSaved: false });
  }

  removeAllFromDeck = (cardId) => {
    const { deckCards } = this.state;
    const removedCard = deckCards.find(c => c._id === cardId);
    let newDeckCards = deckCards.filter(c => c._id !== cardId);

    // If removing a dual-sided field card, also remove its partner
    if (removedCard && removedCard.cardType === "Field") {
      const dualSide = getDualSideName(removedCard);
      if (dualSide) {
        newDeckCards = newDeckCards.filter(c => (c.cardName || c.name) !== dualSide);
      }
    }

    this.setState({ deckCards: newDeckCards, deckDirty: true, justSaved: false });
  }

  addCardToSideDeck = (card) => {
    const { sideDeckCards } = this.state;
    if (sideDeckCards.length >= 15) {
      this.showToast("Side deck max is 15 cards");
      return;
    }
    if (!isUnlimitedCard(card)) {
      const copyCount = sideDeckCards.filter(c => c._id === card._id).length;
      if (copyCount >= 3) {
        this.showToast("Max 3 copies in side deck");
        return;
      }
    }
    this.setState({ sideDeckCards: [...sideDeckCards, card], deckDirty: true, justSaved: false });
  }

  removeCardFromSideDeck = (cardId) => {
    const { sideDeckCards } = this.state;
    const idx = sideDeckCards.findIndex(c => c._id === cardId);
    if (idx === -1) return;
    const newSideDeck = [...sideDeckCards];
    newSideDeck.splice(idx, 1);
    this.setState({ sideDeckCards: newSideDeck, deckDirty: true, justSaved: false });
  }

  removeAllFromSideDeck = (cardId) => {
    const { sideDeckCards } = this.state;
    const newSideDeck = sideDeckCards.filter(c => c._id !== cardId);
    this.setState({ sideDeckCards: newSideDeck, deckDirty: true, justSaved: false });
  }

  saveDeck = async () => {
    const { deckName, deckCards, deckImage, apiDeckId, isLoggedIn } = this.state;

    if (!deckName.trim()) {
      this.showToast("Please enter a deck name");
      return;
    }
    const mainCount = deckCards.filter(c => c.cardType !== "Field").length;
    if (mainCount < 50) {
      this.showToast(`Need ${50 - mainCount} more cards (min 50)`);
      return;
    }
    if (mainCount > 60) {
      this.showToast("Too many cards (max 60)");
      return;
    }
    if (!deckCards.some(c => c.cardType === "Field")) {
      this.showToast("Your deck needs a Field card");
      return;
    }

    // If not logged in, show login modal
    if (!isLoggedIn) {
      this.setState({ showLoginModal: true });
      return;
    }

    this.setState({ savingDeck: true });
    const body = {
      name: deckName.trim(),
      image: deckImage,
      deck: deckCards.map(c => c._id),
      isPublic: this.state.deckIsPublic,
    };
    if (this.state.sideDeckCards.length > 0) {
      body.sideDeck = this.state.sideDeckCards.map(c => c._id);
    }

    try {
      let res;
      if (apiDeckId) {
        // Update existing deck
        res = await auth.authFetch(`${constants.DOMAIN}api/v1/decks/${apiDeckId}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
      } else {
        // Create new deck
        res = await auth.authFetch(`${constants.DOMAIN}api/v1/decks/`, {
          method: "POST",
          body: JSON.stringify(body),
        });
      }
      const data = await res.json();
      if (data.error) {
        this.setState({ savingDeck: false });
        this.showToast(data.error);
        return;
      }
      this.setState({ apiDeckId: data._id, savingDeck: false, deckDirty: false, justSaved: true });
      if (this.savedTimeout) clearTimeout(this.savedTimeout);
      this.savedTimeout = setTimeout(() => {
        this.setState({ justSaved: false });
      }, 2000);
      await this.fetchMyDecks();
      this.showToast(apiDeckId ? "Deck updated!" : "Deck saved!");
    } catch (err) {
      this.setState({ savingDeck: false });
      // Try to parse error response for max private deck message
      try {
        const errData = await err.response?.json();
        if (errData?.error) {
          this.showToast(errData.error);
          return;
        }
      } catch {}
      this.showToast("Failed to save deck");
    }
  }

  handleLoginSuccess = async (userData) => {
    const user = auth.getUser();
    this.setState({ isLoggedIn: true, currentUser: user, showLoginModal: false });
    window.dispatchEvent(new Event("authChange"));
    await this.fetchMyDecks();
    // Auto-save only if user was building a deck when they triggered login
    if (this.state.deckCards.length > 0) {
      this.saveDeck();
    }
  }

  loadDeck = (deck) => {
    const cards = deck.cards || [];
    const sideDeck = deck.sideDeck || [];
    const isPublic = deck.isPublic !== undefined ? deck.isPublic : true;
    this.setState({
      apiDeckId: deck.id,
      deckName: deck.name,
      deckCards: cards,
      sideDeckCards: sideDeck,
      deckImage: deck.image || "",
      deckIsPublic: isPublic,
      deckDirty: false,
      showExport: false,
      deckSubView: "main",
    });
    this.showToast(`Loaded "${deck.name}"`);
  }

  duplicateDeck = async (deck) => {
    try {
      const res = await auth.authFetch(`${constants.DOMAIN}api/v1/decks/${deck.id}/copy`, {
        method: "POST",
      });
      const newDeck = await res.json();
      const freshDecks = await this.fetchMyDecks();
      // Load the newly copied deck from freshDecks (which has populated cards)
      const saved = freshDecks.find(d => d.id === newDeck._id);
      if (saved) {
        this.loadDeck(saved);
      }
      this.setState({ deckTrayView: "current" });
    } catch {
      this.showToast("Failed to duplicate deck");
    }
  }

  deleteDeck = async (deckId) => {
    try {
      await auth.authFetch(`${constants.DOMAIN}api/v1/decks/${deckId}`, {
        method: "DELETE",
      });
      if (this.state.apiDeckId === deckId) {
        this.setState({ apiDeckId: null });
      }
      await this.fetchMyDecks();
      this.showToast("Deck deleted");
    } catch {
      this.showToast("Failed to delete");
    }
  }

  setDeckPrivate = () => {
    const { savedDecks, apiDeckId } = this.state;
    const existingPrivate = savedDecks.find(d => d.isPublic === false && d.id !== apiDeckId);

    if (existingPrivate) {
      this.setState({ showPrivateSwapModal: true, privateSwapDeck: existingPrivate });
      return;
    }

    this.setState({ deckIsPublic: false, deckDirty: true, justSaved: false });
  }

  confirmPrivateSwap = async () => {
    const { privateSwapDeck } = this.state;
    this.setState({ showPrivateSwapModal: false });

    // Make the existing private deck public
    try {
      await auth.authFetch(`${constants.DOMAIN}api/v1/decks/${privateSwapDeck.id}`, {
        method: "PATCH",
        body: JSON.stringify({ isPublic: true }),
      });
    } catch {
      this.showToast("Failed to update — try again");
      this.setState({ privateSwapDeck: null });
      return;
    }

    // Set current deck to private and save immediately
    this.setState({ deckIsPublic: false, privateSwapDeck: null }, () => {
      this.saveDeck();
    });
  }

  clearDeck = () => {
    this.setState({ showClearModal: true });
  }

  confirmClearDeck = () => {
    this.setState({ deckCards: [], sideDeckCards: [], deckImage: "", showClearModal: false, showExport: false, deckDirty: true, justSaved: false, deckSubView: "main" });
    this.showToast("Deck cleared");
  }

  newDeck = () => {
    this.setState({
      apiDeckId: null,
      deckName: "My Deck",
      deckCards: [],
      sideDeckCards: [],
      deckImage: "",
      deckIsPublic: true,
      showExport: false,
      deckSubView: "main",
    });
  }

  // ========================
  // EXPORT DECK
  // ========================

  exportDeck = () => {
    const uniqueCards = getUniqueCards(this.state.deckCards);
    if (uniqueCards.length === 0) {
      this.showToast("Deck is empty");
      return;
    }

    const text = getExportText(this.state.deckCards, this.state.sideDeckCards);
    navigator.clipboard.writeText(text).then(() => {
      if (this.exportCopiedTimeout) clearTimeout(this.exportCopiedTimeout);
      this.setState({ exportCopied: true });
      this.exportCopiedTimeout = setTimeout(() => {
        this.setState({ exportCopied: false });
      }, 2000);
    }).catch(() => {
      this.showToast("Failed to copy");
    });
  }

  // ========================
  // RESULTS SORTING
  // ========================

  handleSortChange = (e) => {
    const value = e.target.value;
    const [sortBy, sortDirection] = value.split("-");
    this.setState({ sortBy, sortDirection });
  }

  // ========================
  // RARITY EFFECTS
  // ========================

  getRarityClass = (rarity) => {
    if (!this.state.showRarityEffects) return "";
    switch (rarity) {
      case "Secret Rare": return "rarity-secret";
      case "Legendary Rare": return "rarity-legendary";
      case "Super Rare": return "rarity-super";
      default: return "";
    }
  }

  toggleRarityEffects = () => {
    const newValue = !this.state.showRarityEffects;
    this.setState({ showRarityEffects: newValue });
    localStorage.setItem("showRarityEffects", newValue.toString());
  }

  handleCardMouseMove = (e, rarity) => {
    if (rarity !== "Secret Rare") return;
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    card.style.setProperty("--shine-x", `${x}%`);
    card.style.setProperty("--shine-y", `${y}%`);
  }

  handleCardMouseLeave = (e, rarity) => {
    if (rarity !== "Secret Rare") return;
    const card = e.currentTarget;
    card.style.setProperty("--shine-x", "50%");
    card.style.setProperty("--shine-y", "50%");
  }

  // ========================
  // RENDER HELPERS
  // ========================

  renderMultiSelect(id, label, options, stateKey) {
    const selectedValues = this.state[stateKey];
    const hasValue = selectedValues.length > 0;

    return (
      <FormControl variant="outlined" fullWidth className={hasValue ? "has-value" : ""}>
        <InputLabel id={`${id}-label`} shrink={hasValue}>{label}</InputLabel>
        <Select
          labelId={`${id}-label`}
          id={id}
          multiple
          value={selectedValues}
          onChange={this.handleMultiSelectChange(stateKey)}
          label={label}
          displayEmpty
          notched={hasValue}
          renderValue={(selected) => {
            if (selected.length === 0) return null;
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </div>
            );
          }}
          MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              <Checkbox checked={selectedValues.indexOf(option) > -1} />
              <ListItemText primary={option} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  renderSearchPanel() {
    const { search, characteristic, cardEffect, resultsLoading, results, hasSearched } = this.state;
    const { darkMode } = this.state;

    const showAbilityCost = this.state.typeQuery.includes("Ability");
    const showTurnCount = this.state.typeQuery.length === 0 ||
      this.state.typeQuery.includes("Character") ||
      this.state.typeQuery.includes("Strategy");

    const hasFilters = this.hasFilters();

    return (
      <div className="deck-builder-search">
        <div className="filter-panel">
          <h1 className="deck-builder-title">Primal TCG Deck Builder</h1>
          <div className="filter-header">
            <h2 className="filter-header-label">Filters</h2>
            <div className="filter-header-actions">
              <button
                className="theme-toggle-btn"
                onClick={() => {
                  const newMode = !darkMode;
                  this.setState({ darkMode: newMode });
                  localStorage.setItem("searchPageTheme", newMode ? "dark" : "light");
                }}
                aria-label={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
              </button>
              <button
                className={`rarity-effects-btn ${this.state.showRarityEffects ? "active" : ""}`}
                onClick={this.toggleRarityEffects}
                aria-label={this.state.showRarityEffects ? "Disable Rarity Shine" : "Enable Rarity Shine"}
                title={this.state.showRarityEffects ? "Disable Rarity Shine" : "Enable Rarity Shine"}
              >
                <CropPortraitIcon className="card-icon" />
                {this.state.showRarityEffects && <FlareIcon className="sparkle-icon" />}
              </button>
              <button
                className="clear-filters-btn"
                onClick={this.resetFilters}
                disabled={!hasFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>

          <div className="search-row">
            <FormControl variant="outlined" fullWidth>
              <TextField
                variant="outlined"
                label="Search by card name..."
                value={search}
                autoComplete="off"
                onChange={(e) => this.setState({ search: e.target.value })}
              />
            </FormControl>
          </div>

          <div className="filter-grid">
            {this.renderMultiSelect("elements", "Element", ELEMENT_NAMES, "elementsQuery")}
            {this.renderMultiSelect("rarity", "Rarity", RARITY_OPTIONS, "rarityQuery")}
            {this.renderMultiSelect("cardType", "Card Type", CARD_TYPES, "typeQuery")}
            {showTurnCount && this.renderMultiSelect("turnCount", "Turn Count", TURN_COUNTS, "turnCountQuery")}
            {this.renderMultiSelect("combat", "Combat Attribute", COMBAT_ATTRIBUTES, "combatQuery")}
            {this.renderMultiSelect("cardSet", "Card Set", CARD_SETS, "cardSetQuery")}
            {showAbilityCost && this.renderMultiSelect("abilityCost", "Ability Cost", ABILITY_COSTS, "abilityCostQuery")}
            {showAbilityCost && this.renderMultiSelect("abilityCostElement", "Cost Element", ELEMENT_NAMES, "abilityCostElementQuery")}
          </div>

          <div className="filter-options">
            <FormControlLabel
              control={
                <Switch
                  checked={this.state.dualElementsOnly}
                  onChange={(e) => this.setState({ dualElementsOnly: e.target.checked })}
                  color="primary"
                />
              }
              label="Dual Elements Only (match ALL selected)"
              disabled={this.state.elementsQuery.length < 2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={this.state.hideKickstarter}
                  onChange={(e) => this.setState({ hideKickstarter: e.target.checked })}
                  color="primary"
                />
              }
              label="Hide Kickstarter Cards"
            />
          </div>

          <div className="text-filters-row">
            <FormControl variant="outlined" fullWidth>
              <TextField
                variant="outlined"
                label="Characteristics..."
                value={characteristic}
                autoComplete="off"
                onChange={(e) => this.setState({ characteristic: e.target.value })}
              />
            </FormControl>
            <FormControl variant="outlined" fullWidth>
              <TextField
                variant="outlined"
                label="Card effect..."
                value={cardEffect}
                autoComplete="off"
                onChange={(e) => this.setState({ cardEffect: e.target.value })}
              />
            </FormControl>
          </div>
        </div>

        <div className="deck-search-results">
          <div className="results-header">
            <div className="results-header-left">
              <h2 className="deck-results-title">Results</h2>
              {hasSearched && !resultsLoading && results.length > 0 && (
                <span className="results-count">
                  {results.length} card{results.length !== 1 ? "s" : ""} found
                </span>
              )}
            </div>
            {hasSearched && results.length > 1 && !resultsLoading && (
              <div className="results-sort">
                <label htmlFor="db-sort-select" className="sort-label">Sort by:</label>
                <Select
                  id="db-sort-select"
                  className="sort-select"
                  value={`${this.state.sortBy}-${this.state.sortDirection}`}
                  onChange={this.handleSortChange}
                  aria-label="Sort results"
                  disableUnderline
                  MenuProps={{
                    PaperProps: {
                      style: {
                        backgroundColor: darkMode ? "#252540" : "#fff",
                        color: darkMode ? "#fff" : "#333",
                      }
                    }
                  }}
                >
                  <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                  <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                  <MenuItem value="rarity-asc">Rarity (Highest first)</MenuItem>
                  <MenuItem value="rarity-desc">Rarity (Lowest first)</MenuItem>
                  {showTurnCount && <MenuItem value="turnCount-asc">Turn Count (Low-High)</MenuItem>}
                  {showTurnCount && <MenuItem value="turnCount-desc">Turn Count (High-Low)</MenuItem>}
                  <MenuItem value="cardNumber-asc">Card Number (Ascending)</MenuItem>
                  <MenuItem value="cardNumber-desc">Card Number (Descending)</MenuItem>
                </Select>
              </div>
            )}
          </div>

          {resultsLoading && (
            <div className="loading-container" role="status" aria-live="polite">
              <CircularProgress size={40} aria-hidden="true" />
              <p>Searching cards...</p>
            </div>
          )}

          {!resultsLoading && !hasSearched && (
            <div className="initial-state">
              <h3>Use the filters above to search for cards</h3>
            </div>
          )}

          {!resultsLoading && hasSearched && results.length === 0 && (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <h3>No cards found</h3>
              <p>Try adjusting your search filters</p>
              {this.state.showSuggestions && this.state.suggestions.length > 0 && (
                <div className="suggestions-container">
                  <p className="suggestions-label">Did you mean?</p>
                  <div className="suggestions-list">
                    {this.state.suggestions.map((name) => (
                      <button
                        key={name}
                        className="suggestion-btn"
                        onClick={() => this.handleSuggestionClick(name)}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!resultsLoading && results.length > 0 && (
            <>
              <div className="results-grid">
                {sortResults(results, this.state.sortBy, this.state.sortDirection, RARITY_ORDER).map((card) => {
                  const isSide = this.state.deckSubView === "side";
                  const activeCards = isSide ? this.state.sideDeckCards : this.state.deckCards;
                  const count = getCardCount(activeCards, card._id);
                  const maxCopies = card.cardType === "Field" ? 1 : isUnlimitedCard(card) ? Infinity : 3;
                  const addFn = isSide ? this.addCardToSideDeck : this.addCardToDeck;
                  const removeFn = isSide ? this.removeCardFromSideDeck : this.removeCardFromDeck;
                  return (
                    <article
                      key={card._id}
                      className={`result-card ${this.getRarityClass(card.rarity)}`}
                      onClick={() => this.openModal(card)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this.openModal(card); } }}
                      onMouseMove={(e) => this.handleCardMouseMove(e, card.rarity)}
                      onMouseLeave={(e) => this.handleCardMouseLeave(e, card.rarity)}
                      title={`${card.name} — Click to view`}
                      tabIndex={0}
                    >
                      <LazyImage
                        src={`${constants.DOMAIN}${card.image}`}
                        alt={card.name}
                      />
                      <button
                        className={`result-card-cover-btn ${this.state.deckImage === card.image ? "active" : ""}`}
                        onClick={(e) => { e.stopPropagation(); this.setState({ deckImage: card.image, deckDirty: true, justSaved: false }); }}
                        aria-label={this.state.deckImage === card.image ? "Current cover image" : `Set ${card.name} as cover image`}
                        title={this.state.deckImage === card.image ? "Cover image" : "Set as cover"}
                      >
                        {this.state.deckImage === card.image ? <StarIcon style={{ fontSize: 16 }} /> : <StarBorderIcon style={{ fontSize: 16 }} />}
                      </button>
                      {count > 0 ? (
                        <div className="card-controls-bar" onClick={(e) => e.stopPropagation()}>
                          <button
                            className={count === 1 ? "trash" : ""}
                            onClick={() => removeFn(card._id)}
                            aria-label={`Remove ${card.name}`}
                          >
                            {count === 1 ? <DeleteIcon style={{ fontSize: 16 }} /> : "−"}
                          </button>
                          <span className="card-controls-count">{count}x</span>
                          <button
                            onClick={() => addFn(card)}
                            disabled={count >= maxCopies}
                            aria-label={`Add ${card.name}`}
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          className="quick-add-btn"
                          onClick={(e) => { e.stopPropagation(); addFn(card); }}
                          aria-label={`Add ${card.name} to ${isSide ? "side deck" : "deck"}`}
                        >
                          +
                        </button>
                      )}
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  renderDeckCard(card, count) {
    const maxCopies = card.cardType === "Field" ? 1 : isUnlimitedCard(card) ? Infinity : 3;
    const isCover = this.state.deckImage === card.image;

    return (
      <div key={card._id} className="deck-card-row">
        <button
          className={`deck-card-cover-btn ${isCover ? "active" : ""}`}
          onClick={() => this.setState({ deckImage: card.image, deckDirty: true, justSaved: false })}
          aria-label={isCover ? "Current cover image" : `Set ${card.name} as cover image`}
          title={isCover ? "Cover image" : "Set as cover"}
        >
          {isCover ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        </button>
        <div className="deck-card-thumb-wrap" onClick={() => this.openModal(card)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this.openModal(card); } }} role="button" tabIndex={0} style={{ cursor: "pointer" }} aria-label={`View ${card.name}`}>
          <LazyImage src={`${constants.DOMAIN}${card.image}`} alt={card.name} eager />
        </div>
        <div className="deck-card-info" onClick={() => this.openModal(card)} style={{ cursor: "pointer" }}>
          <div className="deck-card-name" title={card.name}>{card.name}</div>
          <div className="deck-card-meta">
            {card.element && card.element.join(" / ")}
            {card.cardType === "Character" && card.turnCount !== undefined ? ` | T${card.turnCount}` : ""}
          </div>
        </div>
        <div className="deck-card-controls">
          <button
            onClick={() => this.removeCardFromDeck(card._id)}
            aria-label={`Remove one copy of ${card.name}`}
          >
            -
          </button>
          <span className="deck-card-count">{count}</span>
          <button
            onClick={() => this.addCardToDeck(card)}
            disabled={count >= maxCopies}
            aria-label={`Add one copy of ${card.name}`}
          >
            +
          </button>
        </div>
      </div>
    );
  }

  renderSideDeckCard(card, count) {
    const maxCopies = isUnlimitedCard(card) ? Infinity : 3;

    return (
      <div key={card._id} className="deck-card-row">
        <div className="deck-card-thumb-wrap" onClick={() => this.openModal(card)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this.openModal(card); } }} role="button" tabIndex={0} style={{ cursor: "pointer" }} aria-label={`View ${card.name}`}>
          <LazyImage src={`${constants.DOMAIN}${card.image}`} alt={card.name} eager />
        </div>
        <div className="deck-card-info" onClick={() => this.openModal(card)} style={{ cursor: "pointer" }}>
          <div className="deck-card-name" title={card.name}>{card.name}</div>
          <div className="deck-card-meta">
            {card.element && card.element.join(" / ")}
            {card.cardType === "Character" && card.turnCount !== undefined ? ` | T${card.turnCount}` : ""}
          </div>
        </div>
        <div className="deck-card-controls">
          <button
            onClick={() => this.removeCardFromSideDeck(card._id)}
            aria-label={`Remove one copy of ${card.name} from side deck`}
          >
            -
          </button>
          <span className="deck-card-count">{count}</span>
          <button
            onClick={() => this.addCardToSideDeck(card)}
            disabled={count >= maxCopies || this.state.sideDeckCards.length >= 15}
            aria-label={`Add one copy of ${card.name} to side deck`}
          >
            +
          </button>
        </div>
      </div>
    );
  }

  renderTrayTurnGroups(entries) {
    const turnGroups = {};
    for (const u of entries) {
      const turn = u.card.turnCount !== undefined ? u.card.turnCount : "?";
      if (!turnGroups[turn]) turnGroups[turn] = [];
      turnGroups[turn].push(u);
    }
    const sortedTurns = Object.keys(turnGroups).sort((a, b) => Number(a) - Number(b));

    return sortedTurns.map(turn => {
      const group = turnGroups[turn];
      const groupTotal = group.reduce((s, u) => s + u.count, 0);
      return (
        <div key={turn} className="tray-turn-group">
          <div className="tray-turn-label">T{turn} <span className="tray-turn-count">({groupTotal})</span></div>
          {group.map(u => this.renderDeckCard(u.card, u.count))}
        </div>
      );
    });
  }

  renderMyDecksView() {
    const { savedDecks, apiDeckId, isLoggedIn } = this.state;

    if (!isLoggedIn) {
      return (
        <div className="my-decks-empty">
          <p>Sign in to save and manage your decks.</p>
          <button
            className="sign-in-prompt-btn"
            onClick={() => this.setState({ showLoginModal: true })}
          >
            Sign In
          </button>
        </div>
      );
    }

    if (this.state.loadingDecks) {
      return (
        <div className="my-decks-empty">
          <CircularProgress size={28} style={{ color: "rgb(255, 186, 6)" }} />
          <p>Loading decks...</p>
        </div>
      );
    }

    if (savedDecks.length === 0) {
      return (
        <div className="my-decks-empty">
          <p>No saved decks yet.</p>
          <p>Build a deck and hit Save to see it here.</p>
        </div>
      );
    }

    const { deckSortNewest } = this.state;
    const sortedDecks = deckSortNewest ? savedDecks : [...savedDecks].reverse();

    return (
      <>
      <div className="my-decks-sort-bar">
        <button
          className="my-decks-sort-btn"
          onClick={() => this.setState(prev => ({ deckSortNewest: !prev.deckSortNewest }))}
        >
          {deckSortNewest ? "Recent" : "Oldest"}
          <span className="sort-arrow">{deckSortNewest ? " ▼" : " ▲"}</span>
        </button>
      </div>
      <div className="my-decks-grid">
        {sortedDecks.map(d => {
          const cardCount = d.cards ? d.cards.filter(c => c.cardType !== "Field").length : 0;
          const isActive = d.id === apiDeckId;
          return (
            <div
              key={d.id}
              className={`my-deck-card ${isActive ? "active" : ""}`}
              onClick={() => { this.loadDeck(d); this.setState({ deckTrayView: "current" }); }}
            >
              {d.image ? (
                <div className="my-deck-card-image-wrap">
                  <LazyImage src={`${constants.DOMAIN}${d.image}`} alt={d.name} eager />
                </div>
              ) : (
                <div className="my-deck-card-placeholder">
                  <span>No Image</span>
                </div>
              )}
              <div className="my-deck-card-info">
                <div className="my-deck-card-name-row">
                  <span className="my-deck-card-name" title={d.name}>{d.name}</span>
                  <span className={`my-deck-visibility-badge ${d.isPublic === false ? "private" : "public"}`}>
                    {d.isPublic === false ? <><LockIcon style={{ fontSize: 12 }} /> Private</> : <><PublicIcon style={{ fontSize: 12 }} /> Public</>}
                  </span>
                </div>
                <div className="my-deck-card-meta-row">
                  <span className="my-deck-card-count">{cardCount} cards</span>
                  <div className="my-deck-card-actions">
                    <button
                      className="my-deck-card-preview"
                      onClick={(e) => { e.stopPropagation(); this.previewSavedDeck(d); }}
                      aria-label={`Preview ${d.name}`}
                    >
                      View
                    </button>
                    <button
                      className="my-deck-card-preview"
                      onClick={(e) => { e.stopPropagation(); this.openPlaymatSaved(d); }}
                      aria-label={`Table view ${d.name}`}
                    >
                      Table
                    </button>
                    <button
                      className="my-deck-card-duplicate"
                      onClick={(e) => { e.stopPropagation(); this.duplicateDeck(d); }}
                      aria-label={`Duplicate ${d.name}`}
                    >
                      Duplicate
                    </button>
                    <button
                      className="my-deck-card-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.setState({ showDeleteModal: true, deleteDeckTarget: d });
                      }}
                      aria-label={`Delete ${d.name}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </>
    );
  }

  renderDeckTray() {
    const { deckName, showDeckTray, deckTrayView } = this.state;
    const stats = getDeckStats(this.state.deckCards);
    const uniqueCards = getUniqueCards(this.state.deckCards);

    const characters = uniqueCards.filter(u => u.card.cardType === "Character");
    const strategies = uniqueCards.filter(u => u.card.cardType === "Strategy");
    const abilities = uniqueCards.filter(u => u.card.cardType === "Ability");
    const fieldCards = uniqueCards.filter(u => u.card.cardType === "Field");

    let countClass = "count-under";
    if (stats.total >= 50 && stats.total <= 60) countClass = "count-valid";
    else if (stats.total > 60) countClass = "count-over";

    const mobileClass = showDeckTray ? "mobile-visible" : "";

    return (
      <>
        {showDeckTray && <div className="deck-tray-overlay visible" onClick={() => this.setState({ showDeckTray: false, showExport: false })} />}
        <div className={`deck-tray ${mobileClass}`}>
          <button
            className="deck-tray-close"
            onClick={() => this.setState({ showDeckTray: false, showExport: false })}
            style={{ display: showDeckTray ? "block" : "none" }}
            aria-label="Close deck tray"
          >
            <CloseIcon />
          </button>

          {/* Tab switcher */}
          <div className="deck-tray-tabs" role="tablist">
            <button
              className={`deck-tray-tab ${deckTrayView === "current" ? "active" : ""}`}
              onClick={() => this.setState({ deckTrayView: "current" })}
              role="tab"
              aria-selected={deckTrayView === "current"}
            >
              Current Deck
            </button>
            <button
              className={`deck-tray-tab ${deckTrayView === "collection" ? "active" : ""}`}
              onClick={() => this.setState({ deckTrayView: "collection" })}
              role="tab"
              aria-selected={deckTrayView === "collection"}
            >
              My Decks {this.state.savedDecks.length > 0 && <span className="tab-badge">{this.state.savedDecks.length}</span>}
            </button>
          </div>

          {deckTrayView === "current" ? (
            <>
              {/* Scrollable deck body */}
              <div className="deck-tray-body">
                <div className="deck-tray-header">
                  <input
                    className="deck-name-input"
                    type="text"
                    value={deckName}
                    onChange={(e) => this.setState({ deckName: e.target.value, deckDirty: true, justSaved: false })}
                    placeholder="Deck Name"
                    maxLength={60}
                    aria-label="Deck name"
                  />
                  <div className="deck-count-indicator">
                    <span className={`deck-count-badge ${countClass}`}>
                      {stats.total}/{stats.total < 50 ? 50 : 60}
                    </span>
                    <span className="field-count">Field: {stats.field}/1</span>
                  </div>

                  {/* Sub-tabs: Main Deck / Side Deck */}
                  <div className="deck-sub-tabs">
                    <button
                      className={`deck-sub-tab ${this.state.deckSubView === "main" ? "active" : ""}`}
                      onClick={() => this.setState({ deckSubView: "main" })}
                    >
                      Main Deck
                    </button>
                    <button
                      className={`deck-sub-tab ${this.state.deckSubView === "side" ? "active" : ""}`}
                      onClick={() => this.setState({ deckSubView: "side" })}
                    >
                      Side Deck <span className="side-deck-count">{this.state.sideDeckCards.length}/15</span>
                    </button>
                  </div>

                  {(uniqueCards.length > 0 || this.state.sideDeckCards.length > 0) && this.state.deckSubView === "main" && (
                    <div className="deck-tray-header-actions">
                      <label className="tray-group-toggle">
                        <input
                          type="checkbox"
                          checked={this.state.trayGroupByTurn}
                          onChange={(e) => this.setState({ trayGroupByTurn: e.target.checked })}
                        />
                        <span className="tray-toggle-slider" />
                        <span className="tray-toggle-text">Group by Turn</span>
                      </label>
                      <button className="clear-deck-btn" onClick={this.clearDeck} aria-label="Clear all cards from deck">
                        Clear
                      </button>
                    </div>
                  )}
                </div>

                {this.state.deckSubView === "main" ? (
                  <>
                    {uniqueCards.length === 0 ? (
                      <div className="deck-tray-empty">
                        <p>Search for cards and click to add them to your deck.</p>
                      </div>
                    ) : (
                      <>
                        {characters.length > 0 && (
                          <div className="deck-section">
                            <div className="deck-section-header">
                              <h4>Characters</h4>
                              <span className="deck-section-count">{stats.characters}</span>
                            </div>
                            {this.state.trayGroupByTurn ? (
                              this.renderTrayTurnGroups(characters)
                            ) : (
                              characters.map(u => this.renderDeckCard(u.card, u.count))
                            )}
                          </div>
                        )}

                        {strategies.length > 0 && (
                          <div className="deck-section">
                            <div className="deck-section-header">
                              <h4>Strategies</h4>
                              <span className="deck-section-count">{stats.strategies}</span>
                            </div>
                            {this.state.trayGroupByTurn ? (
                              this.renderTrayTurnGroups(strategies)
                            ) : (
                              strategies.map(u => this.renderDeckCard(u.card, u.count))
                            )}
                          </div>
                        )}

                        {abilities.length > 0 && (
                          <div className="deck-section">
                            <div className="deck-section-header">
                              <h4>Abilities</h4>
                              <span className="deck-section-count">{stats.abilities}</span>
                            </div>
                            {abilities.map(u => this.renderDeckCard(u.card, u.count))}
                          </div>
                        )}

                        <div className="deck-section">
                          <div className="deck-section-header">
                            <h4>Field</h4>
                            <span className="deck-section-count">{stats.field}</span>
                          </div>
                          {fieldCards.length > 0 ? (
                            fieldCards.map(u => this.renderDeckCard(u.card, u.count))
                          ) : null}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {this.state.sideDeckCards.length === 0 ? (
                      <div className="deck-tray-empty">
                        <p>Search for cards and click + to add them to your side deck (max 15).</p>
                      </div>
                    ) : (
                      <>
                        {getUniqueCards(this.state.sideDeckCards).map(u => this.renderSideDeckCard(u.card, u.count))}
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Sticky footer */}
              <div className="deck-tray-footer">
                {(() => {
                  const { deckCards, savingDeck, deckName, isLoggedIn, apiDeckId, deckDirty, justSaved } = this.state;
                  const mainCount = deckCards.filter(c => c.cardType !== "Field").length;
                  const hasField = deckCards.some(c => c.cardType === "Field");
                  const validSize = mainCount >= 50 && mainCount <= 60;
                  const isUpdate = !!apiDeckId;
                  const canSave = !savingDeck && deckName.trim() && hasField && validSize && (!isUpdate || deckDirty);
                  const hint = !hasField && deckCards.length > 0 ? "Needs a Field card"
                    : mainCount > 0 && mainCount < 50 ? `Need ${50 - mainCount} more card${50 - mainCount === 1 ? "" : "s"} (min 50)`
                    : mainCount > 60 ? `${mainCount - 60} card${mainCount - 60 === 1 ? "" : "s"} over limit (max 60)`
                    : null;
                  const label = savingDeck ? "Saving..."
                    : justSaved ? "Deck Saved!"
                    : isUpdate ? "Update Deck"
                    : "Save Deck";
                  return (
                    <button
                      className={`save-deck-btn ${!isLoggedIn ? "needs-login" : ""} ${justSaved ? "saved" : ""} ${hint ? "invalid" : ""}`}
                      onClick={this.saveDeck}
                      disabled={!canSave}
                    >
                      {!isLoggedIn && <LockIcon style={{ fontSize: 16, marginRight: 6 }} />}
                      {label}
                      {!isLoggedIn && (
                        <span className="btn-sub-text">(Sign in required)</span>
                      )}
                    </button>
                  );
                })()}

                {(() => {
                  const { deckCards } = this.state;
                  const mainCount = deckCards.filter(c => c.cardType !== "Field").length;
                  const hasField = deckCards.some(c => c.cardType === "Field");
                  const hint = !hasField && deckCards.length > 0 ? "Needs a Field card"
                    : mainCount > 0 && mainCount < 50 ? `Need ${50 - mainCount} more card${50 - mainCount === 1 ? "" : "s"} (min 50)`
                    : mainCount > 60 ? `${mainCount - 60} card${mainCount - 60 === 1 ? "" : "s"} over limit (max 60)`
                    : null;
                  return hint ? <p className="field-card-hint"><WarningIcon style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }} />{hint}</p> : null;
                })()}

                {this.state.isLoggedIn && (
                  <div className="deck-visibility-toggle">
                    <button
                      className={`visibility-btn ${this.state.deckIsPublic ? "active" : ""}`}
                      onClick={() => this.setState({ deckIsPublic: true, deckDirty: true, justSaved: false })}
                      title="Visible on Community Decks page"
                    >
                      <PublicIcon style={{ fontSize: 16 }} /> Public
                    </button>
                    <button
                      className={`visibility-btn ${!this.state.deckIsPublic ? "active" : ""}`}
                      onClick={this.setDeckPrivate}
                      title="Only you can see this deck (max 1 private deck)"
                    >
                      <LockIcon style={{ fontSize: 16 }} /> Private
                    </button>
                  </div>
                )}

                <div className="footer-secondary-row">
                  <button
                    className="preview-deck-btn"
                    onClick={this.previewCurrentDeck}
                    disabled={this.state.deckCards.length === 0}
                  >
                    Breakdown
                  </button>
                  <button
                    className="table-view-btn"
                    onClick={this.openPlaymatCurrent}
                    disabled={this.state.deckCards.length === 0}
                  >
                    Table View
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="deck-tray-body">
              <div className="my-decks-header-row">
                <button className="new-deck-btn" onClick={() => { this.newDeck(); this.setState({ deckTrayView: "current" }); }}>+ New Deck</button>
              </div>
              {this.renderMyDecksView()}
            </div>
          )}
        </div>
      </>
    );
  }

  openPreview = (deckObj) => {
    this.setState({
      previewDeck: deckObj,
      testHandCards: [],
      testHandRemaining: [],
      testHandActive: false,
      testHandSelected: new Set(),
    });
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  }

  previewSavedDeck = (deck) => {
    const cards = deck.cards || [];
    const sideDeck = deck.sideDeck || [];
    this.openPreview({ name: deck.name, cards, sideDeck, image: deck.image });
  }

  closePreview = () => {
    this.setState({
      previewDeck: null,
      testHandCards: [],
      testHandRemaining: [],
      testHandActive: false,
      testHandSelected: new Set(),
    });
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  }

  testHandDraw = () => {
    const { previewDeck } = this.state;
    if (!previewDeck || !previewDeck.cards || previewDeck.cards.length === 0) return;
    const mainDeckCards = previewDeck.cards.filter(c => c.cardType !== "Field");
    if (mainDeckCards.length === 0) return;
    const shuffled = shuffleArray(mainDeckCards);
    const drawCount = Math.min(6, shuffled.length);
    this.setState({
      testHandCards: shuffled.slice(0, drawCount),
      testHandRemaining: shuffled.slice(drawCount),
      testHandActive: true,
      testHandSelected: new Set(),
    });
  }

  testHandDrawOne = () => {
    const { testHandRemaining, testHandCards } = this.state;
    if (testHandRemaining.length === 0) return;
    this.setState({
      testHandCards: [...testHandCards, testHandRemaining[0]],
      testHandRemaining: testHandRemaining.slice(1),
    });
  }

  testHandClose = () => {
    this.setState({
      testHandCards: [],
      testHandRemaining: [],
      testHandActive: false,
      testHandSelected: new Set(),
    });
  }

  testHandToggleSelect = (index) => {
    this.setState(prev => {
      const newSelected = new Set(prev.testHandSelected);
      if (newSelected.has(index)) {
        newSelected.delete(index);
      } else {
        newSelected.add(index);
      }
      return { testHandSelected: newSelected };
    });
  }

  testHandMulligan = () => {
    const { testHandCards, testHandRemaining, testHandSelected } = this.state;
    if (testHandSelected.size === 0) return;

    // Return selected cards to remaining, keep non-selected in hand
    const kept = [];
    const returned = [];
    testHandCards.forEach((card, i) => {
      if (testHandSelected.has(i)) {
        returned.push(card);
      } else {
        kept.push(card);
      }
    });

    // Shuffle returned cards back into remaining
    const newRemaining = shuffleArray([...testHandRemaining, ...returned]);

    // Draw replacements
    const drawCount = Math.min(returned.length, newRemaining.length);
    const drawn = newRemaining.slice(0, drawCount);
    const leftover = newRemaining.slice(drawCount);

    this.setState({
      testHandCards: [...kept, ...drawn],
      testHandRemaining: leftover,
      testHandSelected: new Set(),
    });
  }

  previewCurrentDeck = () => {
    const { deckName, deckCards, deckImage, sideDeckCards } = this.state;
    this.openPreview({ name: deckName, cards: deckCards, sideDeck: sideDeckCards, image: deckImage });
  }

  openPlaymat = (deckObj) => {
    this.setState({ showPlaymat: true, playmatDeck: deckObj });
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  }

  closePlaymat = () => {
    this.setState({ showPlaymat: false, playmatDeck: null });
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  }

  openPlaymatCurrent = () => {
    const { deckName, deckCards, deckImage, sideDeckCards } = this.state;
    this.openPlaymat({ name: deckName, cards: deckCards, sideDeck: sideDeckCards, image: deckImage });
  }

  openPlaymatSaved = (deck) => {
    const cards = deck.cards || [];
    const sideDeck = deck.sideDeck || [];
    this.openPlaymat({ name: deck.name, cards, sideDeck, image: deck.image });
  }

  render() {
    const { darkMode, deckCards, selectedCard, results, modalFromPreview } = this.state;
    const stats = getDeckStats(deckCards);

    return (
      <div className={`DeckBuilder ${darkMode ? "dark-mode" : "light-mode"}`}>
        <a className="skip-nav" href="#main-content">Skip to main content</a>
        <Helmet>
          <title>Deck Builder | Primal Trading Card Game</title>
          <meta name="description" content="Build your Primal TCG deck online. Search cards, add them to your deck, and share your creation with the community." />
          <meta property="og:title" content="Primal TCG Deck Builder" />
          <meta property="og:description" content="Build and share your Primal TCG deck online." />
          <meta property="og:image" content="https://pbs.twimg.com/media/GJ38D7mWwAAMXsa?format=jpg&name=medium" />
          <link rel="canonical" href="https://primaltcg.com/build" />
        </Helmet>
        <Navbar />
        <main id="main-content" className="row bkg-red">
          <div className="col-12">
            <div className="deck-builder-layout">
              {this.renderSearchPanel()}
              {this.renderDeckTray()}
            </div>
          </div>
        </main>
        <Footer />

        <CardDetailModal
          card={selectedCard}
          darkMode={darkMode}
          results={this.getSortedResults()}
          modalFromPreview={modalFromPreview}
          onClose={this.closeModal}
          onPrev={this.goToPrevCard}
          onNext={this.goToNextCard}
          onAdd={this.addCardToDeck}
          onRemove={this.removeCardFromDeck}
          onRemoveAll={this.removeAllFromDeck}
          getCardCount={(cardId) => getCardCount(deckCards, cardId)}
          getRarityClass={this.getRarityClass}
          onTouchStart={this.handleTouchStart}
          onTouchEnd={this.handleTouchEnd}
          cardUsageStats={this.state.cardUsageStats}
          deckImage={this.state.deckImage}
          onSetCover={(image) => this.setState({ deckImage: image, deckDirty: true, justSaved: false })}
          onFlipCard={this.handleFlipCard}
        />

        <DeckPreviewModal
          previewDeck={this.state.previewDeck}
          darkMode={darkMode}
          showRarityEffects={this.state.showRarityEffects}
          previewGroupByTurn={this.state.previewGroupByTurn}
          onToggleGroupByTurn={(checked) => this.setState({ previewGroupByTurn: checked })}
          testHandActive={this.state.testHandActive}
          testHandCards={this.state.testHandCards}
          testHandRemaining={this.state.testHandRemaining}
          testHandSelected={this.state.testHandSelected}
          onTestHandDraw={this.testHandDraw}
          onTestHandDrawOne={this.testHandDrawOne}
          onTestHandClose={this.testHandClose}
          onTestHandToggleSelect={this.testHandToggleSelect}
          onTestHandMulligan={this.testHandMulligan}
          onOpenModal={this.openModal}
          onClose={this.closePreview}
        />

        {this.state.showPlaymat && (
          <PlaymatView
            deck={this.state.playmatDeck}
            onOpenModal={this.openModal}
            onClose={this.closePlaymat}
            onShowToast={this.showToast}
          />
        )}

        <ConfirmModal
          isOpen={this.state.showPrivateSwapModal && !!this.state.privateSwapDeck}
          darkMode={darkMode}
          onClose={() => this.setState({ showPrivateSwapModal: false, privateSwapDeck: null })}
          icon={<LockIcon style={{ fontSize: 32 }} />}
          iconVariant="default"
          title="Swap Private Deck?"
          message={
            this.state.privateSwapDeck
              ? <><strong>"{this.state.privateSwapDeck.name}"</strong> is currently your private deck. You can only have 1 private deck at a time.</>
              : ""
          }
          subMessage={
            this.state.privateSwapDeck
              ? `This will make "${this.state.privateSwapDeck.name}" public and set your current deck to private instead.`
              : ""
          }
          confirmLabel="Swap"
          confirmVariant="confirm"
          onConfirm={this.confirmPrivateSwap}
        />

        <ConfirmModal
          isOpen={this.state.showDeleteModal && !!this.state.deleteDeckTarget}
          darkMode={darkMode}
          onClose={() => this.setState({ showDeleteModal: false, deleteDeckTarget: null })}
          icon={<DeleteIcon style={{ fontSize: 32 }} />}
          iconVariant="danger"
          title="Delete Deck?"
          message={
            this.state.deleteDeckTarget
              ? <>Are you sure you want to delete <strong>"{this.state.deleteDeckTarget.name}"</strong>?</>
              : ""
          }
          subMessage="This action can't be undone."
          confirmLabel="Delete"
          confirmVariant="danger"
          onConfirm={() => {
            this.deleteDeck(this.state.deleteDeckTarget.id);
            this.setState({ showDeleteModal: false, deleteDeckTarget: null });
          }}
        />

        <ConfirmModal
          isOpen={this.state.showClearModal}
          darkMode={darkMode}
          onClose={() => this.setState({ showClearModal: false })}
          icon={<DeleteIcon style={{ fontSize: 32 }} />}
          iconVariant="danger"
          title="Clear Deck?"
          message="This will remove all cards from your current deck."
          confirmLabel="Clear All"
          confirmVariant="danger"
          onConfirm={this.confirmClearDeck}
        />

        {this.state.showBackToTop && (
          <button
            className="back-to-top-btn"
            onClick={this.scrollToTop}
            title="Back to top"
            aria-label="Scroll back to top of page"
          >
            <KeyboardArrowUpIcon aria-hidden="true" />
          </button>
        )}

        <LoginModal
          isOpen={this.state.showLoginModal}
          onClose={() => this.setState({ showLoginModal: false })}
          onLoginSuccess={this.handleLoginSuccess}
          darkMode={this.state.darkMode}
        />

        {/* Mobile deck tray toggle */}
        <button
          className="deck-tray-toggle"
          onClick={() => this.setState({ showDeckTray: !this.state.showDeckTray })}
          aria-label={`Open deck tray, ${stats.total + stats.field} cards`}
          aria-expanded={this.state.showDeckTray}
        >
          <span className="toggle-count">{stats.total + stats.field}</span>
          <span className="toggle-label">Deck</span>
        </button>

        {this.state.toast && (
          <div className="deck-toast" role="alert" aria-live="polite">{this.state.toast}</div>
        )}
      </div>
    );
  }
}
